package empiresmp.feature.shop.viewmodel

import empiresmp.feature.shop.backend.BackendApi
import empiresmp.feature.shop.backend.model.PreCheckData
import empiresmp.feature.shop.backend.model.PublicServerShop
import empiresmp.feature.shop.backend.model.PublicShopItemData
import empiresmp.feature.shop.backend.model.ServerType
import empiresmp.feature.shop.backend.model.ShopItemId
import empiresmp.feature.shop.bridge.TelegramBridge
import empiresmp.feature.shop.bridge.sendData
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class ShopViewModel(private val initialUsername: String) {
    val state = MutableStateFlow<State>(State.Loading)
    private inline fun <reified K : T, reified T> MutableStateFlow<T>.updateAs(
        block: (K) -> T
    ) = update { state -> (state as? K)?.let(block) ?: state }

    fun onUsernameChanged(value: String) {
        state.updateAs { stateLoaded: State.Loaded ->
            stateLoaded.copy(username = value)
        }
    }

    fun onSelectedItemChanged(id: ShopItemId?) {
        state.updateAs { stateLoaded: State.Loaded ->
            val selectedItem = stateLoaded
                .shop
                .shopItemDatas
                .firstOrNull { itemData -> itemData.id == id }
                ?: return@updateAs stateLoaded.copy(selectedItemState = SelectedItemState.Unselected)
            stateLoaded.copy(selectedItemState = SelectedItemState.Selected(selectedItem))
        }
    }

    @Suppress("UnusedPrivateMember")
    fun onConfirm() {
        GlobalScope.launch {
            val loadedState = state.first() as? State.Loaded
                ?: return@launch
            val selectedItem = loadedState.selectedItemState as? SelectedItemState.Selected
                ?: return@launch
            val preCheckData = PreCheckData(
                shopItemId = selectedItem.selectedItemData.id,
                telegramUserId = null,
                minecraftName = loadedState.username,
                serverType = ServerType.SMP
            )
            val preCheckResult = BackendApi.makePreCheck(preCheckData)
                .onFailure { it.printStackTrace() }
                .getOrNull()
                ?: return@launch
            val tb = TelegramBridge.stateFlow.first()
            tb.sendData(preCheckResult)
            tb.close()

            state.updateAs { stateLoaded: State.Loaded ->
                val itemState = stateLoaded.selectedItemState as? SelectedItemState.Selected
                    ?: return@updateAs stateLoaded
                stateLoaded.copy(
                    selectedItemState = SelectedItemState.PreOrdered(
                        selectedItemData = itemState.selectedItemData,
                        uniquePhrase = preCheckResult.uniquePhrase
                    )
                )
            }
        }
    }

    init {
        GlobalScope.launch {
            runCatching {
                BackendApi.getServerShop(ServerType.SMP)
            }.onSuccess { shop ->
                state.emit(
                    State.Loaded(
                        shop = shop,
                        username = initialUsername,
                        selectedItemState = SelectedItemState.Unselected
                    )
                )
            }.onFailure {
                state.emit(State.Error)
            }
        }
    }

    sealed interface State {

        data object Loading : State
        data class Loaded(
            val username: String,
            val selectedItemState: SelectedItemState,
            val shop: PublicServerShop
        ) : State

        data object Error : State
    }

    sealed interface SelectedItemState {
        data object Unselected : SelectedItemState

        data class Selected(
            val selectedItemData: PublicShopItemData
        ) : SelectedItemState

        data class PreOrdered(
            val selectedItemData: PublicShopItemData,
            val uniquePhrase: String
        ) : SelectedItemState
    }
}
